import classNames from 'classnames';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import { PeopleApi, UserDTO } from '../api';
import { useLegalMutationsAllowed, useProfile } from '../hooks';
import { toBirthdateInputValue } from '../utils';

export const ClientSettingsPage = () => {
  const [tab, setTab] = useState<'settings' | 'cards'>('settings');
  const queryClient = useQueryClient();
  const { allowed: legalMutationsAllowed, requireConsent } =
    useLegalMutationsAllowed();
  const { mutate } = useMutation({
    mutationFn: PeopleApi.updateMe,
    onSuccess() {
      queryClient.refetchQueries([PeopleApi.KEY, 'me']);
    },
  });
  const user = useProfile(true);
  const { handleSubmit, register, reset } = useForm<UserDTO>();

  useEffect(() => {
    if (user) {
      reset({
        address_text: user?.address?.[0]?.address_text ?? '',
        birthdate: toBirthdateInputValue(user?.birthdate),
        midname: user?.midname ?? '',
        name: user?.name ?? '',
        surname: user?.surname ?? '',
      });
    }
  }, [user, reset]);

  const onSubmit: SubmitHandler<UserDTO> = useCallback(
    (data) => {
      if (!legalMutationsAllowed) {
        requireConsent();
        return;
      }
      mutate(data);
    },
    [legalMutationsAllowed, mutate, requireConsent],
  );

  return (
    <>
      <div className="mb-[36px] flex gap-[8px]">
        <button
          className={classNames(
            'py-[4px] px-[16px] rounded-[10px] leading-normal border border-white transition-all',
            {
              'bg-white': tab === 'settings',
            },
          )}
          onClick={() => setTab('settings')}
          type="button"
        >
          Личные данные
        </button>
        <button
          className={classNames(
            'py-[4px] px-[16px] rounded-[10px] leading-normal border border-white transition-all',
            {
              'bg-white': tab === 'cards',
            },
          )}
          onClick={() => setTab('cards')}
          type="button"
        >
          Методы оплаты
        </button>
      </div>

      {tab === 'settings' && (
        <div className="grid gap-[36px] lg:grid-cols-2">
          <div className="overflow-hidden rounded-[16px] bg-white text-brand-1">
            <header className="rounded-t-lg bg-brand-6 px-[24px] py-[16px]">
              <h2 className="text-[20px] font-bold leading-normal">
                Личные данные
              </h2>
            </header>
            <form
              className="grid gap-[10px] p-[20px]"
              onSubmit={handleSubmit(onSubmit)}
            >
              <input
                {...register('name')}
                className="input"
                name="name"
                placeholder="Имя"
                type="text"
              />
              <input
                {...register('surname')}
                className="input"
                name="surname"
                placeholder="Фамилия"
                type="text"
              />
              <input
                {...register('midname')}
                className="input"
                name="midname"
                placeholder="Отчество"
                type="text"
              />
              <input
                {...register('birthdate')}
                className="input"
                name="birthdate"
                type="date"
              />
              <input
                {...register('address_text')}
                className="input"
                name="address_text"
                placeholder="Адрес (необязательно)"
                type="text"
              />
              <button
                className="button button--block button--primary disabled:opacity-50"
                type="submit"
                disabled={!legalMutationsAllowed}
              >
                Сохранить
              </button>
            </form>
          </div>
        </div>
      )}

      {tab === 'cards' && (
        <div className="grid gap-[36px] lg:grid-cols-2">
          {user?.cards.map((card) => (
            <div className="overflow-hidden rounded-[16px] bg-white text-brand-1">
              <header className="rounded-t-lg bg-brand-6 px-[24px] py-[16px]">
                <h2 className="text-[20px] font-bold leading-normal">Карта</h2>
              </header>
              <div className="p-[20px]">
                <div className="flex justify-between rounded-[10px] border border-brand-1/50 p-[10px]">
                  {card.card_number_masked}
                  <span>
                    {card.card_month_end.toString().padStart(2, '0')}/20
                    {card.card_year_end}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};
